declare global {
  interface Window {
    axon?: {
      (action: "init"): void;
      (action: "track", eventName: string, eventData?: Record<string, unknown>): void;
      operationQueue?: unknown[];
      performOperation?: (...args: unknown[]) => void;
      ts?: number;
      eventKey?: string;
    };
    AXON_EVENT_KEY?: string;
  }
}

export const AXON_EVENT_KEY = "baabbd93-73f9-4821-858d-0243b22f2e00";

// The five funnel events are tracked once, for Meta, at each call site. AppLovin wants the same
// five under its own catalog names, so the Meta tracker mirrors every event through here instead
// of every call site growing a second pixel call.
const AXON_EVENT_NAMES: Record<string, string> = {
  PageView: "page_view",
  ViewContent: "view_item",
  AddToCart: "add_to_cart",
  InitiateCheckout: "begin_checkout",
  Purchase: "purchase",
};

type AxonArgs = [eventName: string, eventData: Record<string, unknown>];

// Same race as the Meta pixel (see lib/fbq.ts): the axon stub is injected by <Script
// strategy="afterInteractive"> in the root layout, so window.axon does NOT exist yet while a mount
// effect runs — and the order-status page fires Purchase synchronously on mount. Buffer early calls
// and replay them in order once the stub appears; the stub's own operationQueue then holds them
// until DeferredThirdParty loads pixel.js at interaction/idle.
const pendingAxonCalls: AxonArgs[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushAttempts = 0;
const MAX_FLUSH_ATTEMPTS = 40; // ~10s at 250ms; the stub normally lands within a tick of hydration

function drainAxonQueue() {
  if (typeof window === "undefined" || typeof window.axon !== "function") return false;
  while (pendingAxonCalls.length > 0) {
    const [eventName, eventData] = pendingAxonCalls.shift() as AxonArgs;
    window.axon("track", eventName, eventData);
  }
  return true;
}

function scheduleDrain() {
  if (flushTimer !== null || pendingAxonCalls.length === 0) return;
  if (flushAttempts >= MAX_FLUSH_ATTEMPTS) return;
  flushAttempts += 1;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (!drainAxonQueue()) scheduleDrain();
  }, 250);
}

// AppLovin's purchase check wants a per-item price, which Meta only carries in `contents`
// ([{ id, quantity, item_price }]). Fall back to content_ids when a call site sends no prices —
// an event without item prices is still worth more than no event.
function axonItems(payload: Record<string, unknown>) {
  const contents = Array.isArray(payload.contents)
    ? (payload.contents as Array<{ id: string; quantity?: number; item_price?: number }>)
    : [];
  if (contents.length > 0) {
    return {
      items: contents.map((line) => ({
        item_id: line.id,
        quantity: line.quantity ?? 1,
        ...(line.item_price !== undefined ? { price: line.item_price } : {}),
      })),
    };
  }

  const contentIds = Array.isArray(payload.content_ids) ? (payload.content_ids as string[]) : [];
  if (contentIds.length === 0) return {};
  return { items: contentIds.map((id) => ({ item_id: id, quantity: 1 })) };
}

// AppLovin matches a conversion to a person by email, the way Meta's advanced matching does. It is
// set from the same call, hashed by AppLovin on arrival, and only ever the address the customer
// typed into our own checkout.
let userEmail: string | null = null;

export function setAxonUserData(email: string | undefined) {
  const clean = email?.trim().toLowerCase();
  if (clean) userEmail = clean;
}

/**
 * Mirror one Meta funnel event to the AppLovin (Axon) pixel. Meta's content_* payload is remapped
 * to Axon's catalog shape; an unmapped event name is ignored.
 */
export function mirrorToAxon(
  metaEventName: string,
  payload: Record<string, unknown>,
  eventId: string,
) {
  if (typeof window === "undefined") return;

  const eventName = AXON_EVENT_NAMES[metaEventName];
  if (!eventName) return;

  const eventData: Record<string, unknown> = {
    event_id: eventId,
    // Axon wants the bare id, not our prefixed event id ("purchase_{orderId}" → "{orderId}"), so a
    // purchase reported here carries the same transaction id the order does everywhere else.
    transaction_id: eventId.replace(/^[a-z]+_/, ""),
    ...(payload.value !== undefined ? { value: payload.value } : {}),
    ...(payload.currency !== undefined ? { currency: payload.currency } : {}),
    ...axonItems(payload),
    ...(userEmail ? { user_data: { email: userEmail } } : {}),
  };

  if (pendingAxonCalls.length === 0 && typeof window.axon === "function") {
    window.axon("track", eventName, eventData);
    return;
  }
  pendingAxonCalls.push([eventName, eventData]);
  if (!drainAxonQueue()) scheduleDrain();
}
